import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Calendar,
  ChevronDown,
  ChevronRight,
  Edit2,
  Save,
  Trash2,
  AlertTriangle,
  Check,
} from "lucide-react";
import { Project, ProjectCreate } from "../../../services/projectService";
import { listResources, addResource, removeResource, ProjectResource } from "../../../services/devopsService";
import { itAssetsService } from "../../../services/itAssetsService";
import { userService } from "../../../services/userService";
import { filterPickableUsers } from "../../../utils/userPickable";
import {
  taskService,
  TaskCreate,
  Task,
  Dependency,
  TaskUpdate,
  TaskResource,
  TaskResourceCreate,
} from "../../../services/taskService";
import { ActualBookingPanel } from "./ActualBookingModal";
import FormControl from "../../../components/common/FormControl";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Skeleton from "../../../components/common/Skeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import Toast, { ToastMessage } from "../../../components/common/Toast";
import { swalError, swalSuccess, swalConfirm } from "../../../components/common/confirm";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from "../../../validation/ValidatedFields";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";

interface ExtraTab {
  id: string;
  label: string;
  icon: any;
  render: () => React.ReactNode;
}

interface ProjectDetailsModalProps {
  project: Project;
  onClose: () => void;
  onSave?: (data: ProjectCreate) => Promise<void> | void;
  extraTabs?: ExtraTab[];
  initialTab?: string;
  // When false (a non-head project member/resource), the modal is a limited,
  // read-only view: only the WBS tab, and only its WBS-Grid + Kanban sub-views.
  canManage?: boolean;
}

// --- HELPER FUNCTIONS ---

// Read-only label/value cell for the General "view" mode.
const KV: React.FC<{ label: string; value: React.ReactNode; full?: boolean }> = ({ label, value, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
    <dd className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap break-words">
      {value === null || value === undefined || value === "" ? "—" : value}
    </dd>
  </div>
);

const addDays = (dateStr: string | null | undefined, days: number) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const diffDays = (
  d1: string | null | undefined,
  d2: string | null | undefined,
) => {
  if (!d1 || !d2) return 0;
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  const diffTime = date2.getTime() - date1.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({
  project,
  onClose,
  onSave,
  extraTabs,
  initialTab,
  canManage = true,
}) => {
  const [activeTab, setActiveTab] = useState(canManage ? (initialTab || "DETAILS") : "WBS");
  const [isEditing, setIsEditing] = useState(false);
  // WBS tab inner view (Gantt / CPM / Grid / Kanban) + the create-task modal.
  const [wbsView, setWbsView] = useState<"GANTT" | "CPM" | "GRID" | "KANBAN" | "VARIANCE">(canManage ? "GANTT" : "GRID");
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  // Project resource plan (high-level men & machines + budgeted hours) — Basic Details tab
  const [planRows, setPlanRows] = useState<ProjectResource[]>([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [planUsers, setPlanUsers] = useState<any[]>([]);
  // Entity users for the Project Lead (project head) picker on Basic Details.
  const [leadUsers, setLeadUsers] = useState<any[]>([]);
  const [newPlan, setNewPlan] = useState<{ resource_kind: 'STAFF' | 'MACHINE'; user_id: number | ''; it_asset_id: number | ''; role: string; rate: number | ''; hours: number | '' }>({ resource_kind: 'STAFF', user_id: '', it_asset_id: '', role: '', rate: '', hours: '' });
  const [isPlanResOpen, setIsPlanResOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [isPlanEditing, setIsPlanEditing] = useState(false);
  const [planToast, setPlanToast] = useState<ToastMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const vhDetails = useFieldValidation();
  const vhNewTask = useFieldValidation();
  const vhEditTask = useFieldValidation();
  // MIXED FORM: only the project-save path is activity-gated (PRM_PROJECTS).
  // The task-create / task-edit paths are ungated — do not gate them.
  const { allowed: canModify, loading: permLoading } = usePermission('PRM_PROJECTS', 'view+modify');
  const [editFormData, setEditFormData] = useState<ProjectCreate>({
    project_code: project.project_code,
    name: project.name,
    description: project.description || "",
    status: project.status,
    baseline_start_date: project.baseline_start_date?.split("T")[0] || "",
    baseline_finish_date: project.baseline_finish_date?.split("T")[0] || "",
  });
  const editFormRef = useRef<HTMLFormElement>(null);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editTab, setEditTab] = useState("GENERAL");

  const [tasks, setTasks] = useState<Task[]>([]);
  const [dependencies, setDependencies] = useState<Dependency[]>([]);
  const [scheduledTasks, setScheduledTasks] = useState<any[]>([]);

  const [newTask, setNewTask] = useState<TaskCreate>({
    project_id: project.id,
    name: "",
    description: "",
    is_milestone: false,
    duration_planned: 1,
    constraint_type: "ASAP",
    wbs_code: "",
  });

  // Dependency creation state
  const [newDependency, setNewDependency] = useState({
    predecessor_id: "",
    type: "FS",
    lag_days: 0,
  });

  const [generalEditMode, setGeneralEditMode] = useState(false);
  const [isAddPredOpen, setIsAddPredOpen] = useState(false);

  // Resource state
  const [isAddResourceOpen, setIsAddResourceOpen] = useState(false);
  const [resources, setResources] = useState<TaskResource[]>([]);
  const [actuals, setActuals] = useState<any[]>([]);
  const [staffOptions, setStaffOptions] = useState<any[]>([]);
  const [assetOptions, setAssetOptions] = useState<any[]>([]);
  const [taskMachineOptions, setTaskMachineOptions] = useState<any[]>([]);
  const [newResource, setNewResource] = useState<TaskResourceCreate>({
    resource_type: "STAFF",
    resource_id: 0,
    quantity: 1,
    effort_hours: 0, // Unused
  });

  useEffect(() => {
    setNewTask((prev) => ({ ...prev, project_id: project.id }));
    setEditFormData({
      project_code: project.project_code,
      name: project.name,
      description: project.description || "",
      status: project.status,
      baseline_start_date: project.baseline_start_date?.split("T")[0] || "",
      baseline_finish_date: project.baseline_finish_date?.split("T")[0] || "",
      budget_amount: project.budget_amount ?? undefined,
      project_manager_id: project.project_manager_id ?? undefined,
    });
    setIsEditing(false);
    vhDetails.reset();
    loadData();
    userService
      .getAllUsers({ page: 1, limit: 500 })
      .then((r: any) => setLeadUsers(filterPickableUsers(r?.data || [])))
      .catch(() => setLeadUsers([]));
  }, [project.id]);

  // Removed local calculation effect

  const loadData = async () => {
    try {
      const [fetchedTasks, fetchedDeps, fetchedSchedule] = await Promise.all([
        taskService.getTasks(project.id),
        taskService.getDependencies(project.id),
        taskService.getProjectSchedule(project.id),
      ]);
      setTasks(fetchedTasks);
      setDependencies(fetchedDeps);
      setScheduledTasks(fetchedSchedule);
    } catch (error) {
      console.error("Failed to load project data:", error);
      swalError("Failed to load project data. Please check console for details.");
    }
  };

  const leadName = (id?: number | null) => {
    const u = leadUsers.find((x: any) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}`.trim() : null;
  };

  const handleSaveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) { swalError(NOT_AUTHORISED_MSG); return; }
    if (!onSave) return;
    const start = editFormData.baseline_start_date || "";
    const finish = editFormData.baseline_finish_date || "";
    const specs: any[] = [
      { name: "name", code: 109, value: editFormData.name, required: true },
      { name: "description", code: 110, value: editFormData.description },
      { name: "baseline_start_date", code: 305, value: start },
      { name: "baseline_finish_date", code: 305, value: finish },
    ];
    if (start && finish) {
      specs.push({
        name: "baseline_finish_date", code: 601, value: "x", required: true,
        additional: { from_date: start, to_date: finish },
      });
    }
    if (!vhDetails.validateAll(specs)) return;
    setSubmitting(true);
    try {
      // Pydantic Optional[date] rejects "" — convert empty-string dates to undefined.
      const payload: ProjectCreate = {
        ...editFormData,
        baseline_start_date: editFormData.baseline_start_date || (undefined as any),
        baseline_finish_date: editFormData.baseline_finish_date || (undefined as any),
      };
      await onSave(payload);
      setIsEditing(false);
    } finally {
      setSubmitting(false);
    }
  };

  const [submittingTask, setSubmittingTask] = useState(false);

  const handleAddTask = async () => {
    const specs: any[] = [
      { name: "name", code: 109, value: newTask.name, required: true },
      { name: "description", code: 110, value: newTask.description },
      { name: "duration_planned", code: 206, value: newTask.duration_planned },
    ];
    if (newTask.sequence_no != null && newTask.sequence_no > 0) {
      specs.push({ name: "sequence_no", code: 202, value: newTask.sequence_no });
    }
    if (!vhNewTask.validateAll(specs)) return;
    setSubmittingTask(true);
    try {
      await taskService.createTask(newTask);
      setNewTask({
        project_id: project.id,
        name: "",
        description: "",
        is_milestone: false,
        duration_planned: 1,
        constraint_type: "ASAP",
        wbs_code: "",
      });
      vhNewTask.reset();
      await loadData();
      setIsCreateTaskOpen(false);
      setActiveTab("WBS");
      setWbsView("GANTT");
      swalSuccess("Task created successfully!");
    } catch (error) {
      console.error("Failed to create task:", error);
      swalError("Failed to create task.");
    } finally {
      setSubmittingTask(false);
    }
  };

  const openEditModal = (task: Task, initialTab = "GENERAL") => {
    setEditingTask(task);
    setEditTab(initialTab);
    setIsEditTaskOpen(true);
    setSwapToSeq("");
    setGeneralEditMode(false);
    vhEditTask.reset();
    // Reset resource state
    setResources([]);
    if (initialTab === "RESOURCES") {
      loadResources(task.id);
    }
  };

  const loadResources = async (taskId: number) => {
    try {
      const [res, act, plan] = await Promise.all([
        taskService.getTaskResources(taskId),
        taskService.getTaskResourceActuals(taskId),
        listResources(project.id).catch(() => []),
      ]);
      setResources(res);
      setActuals(act);
      // Task allocation pickers are gated to the project Resource Plan — you can
      // only put a man or a machine on a task if it is on the project's plan.
      setStaffOptions((plan as any[]).filter((r) => (r.resource_kind || 'STAFF') === 'STAFF'));
      setTaskMachineOptions((plan as any[]).filter((r) => r.resource_kind === 'MACHINE'));
    } catch (error) {
      console.error("Failed to load resources:", error);
    }
  };

  // --- High-level project resource plan (Basic Details) ---
  const fetchPlan = async () => {
    try {
      setPlanLoading(true);
      const [res, u, assets] = await Promise.all([
        listResources(project.id),
        userService.getAllUsers({ page: 1, limit: 200 }).catch(() => ({ data: [] } as any)),
        itAssetsService.list().catch(() => []),
      ]);
      setPlanRows(res as ProjectResource[]);
      setPlanUsers(filterPickableUsers(((u as any).data) || []));
      setAssetOptions(assets as any[]);
    } catch { /* leave plan empty on error */ }
    finally { setPlanLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "RESOURCES") fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, project.id]);

  const openAddPlan = () => {
    setNewPlan({ resource_kind: "STAFF", user_id: "", it_asset_id: "", role: "", rate: "", hours: "" });
    setEditingPlanId(null);
    setIsPlanEditing(true);
    setIsPlanResOpen(true);
  };

  const openEditPlan = (r: ProjectResource) => {
    setNewPlan({
      resource_kind: r.resource_kind === "MACHINE" ? "MACHINE" : "STAFF",
      user_id: r.user_id ?? "",
      it_asset_id: r.it_asset_id ?? "",
      role: r.role_in_project ?? "",
      rate: r.hourly_rate ?? "",
      hours: r.budgeted_hours ?? "",
    });
    setEditingPlanId(r.id);
    setIsPlanEditing(false);
    setIsPlanResOpen(true);
  };

  // Add OR edit — the backend POST upserts on (project, person/machine), so
  // editing role/rate/hours re-posts the same resource and updates it.
  const handleAddPlanResource = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const k = newPlan.resource_kind;
    if (k === "STAFF" && !newPlan.user_id) return;
    if (k === "MACHINE" && !newPlan.it_asset_id) return;
    try {
      await addResource(project.id, {
        resource_kind: k,
        user_id: k === "STAFF" ? Number(newPlan.user_id) : undefined,
        it_asset_id: k === "MACHINE" ? Number(newPlan.it_asset_id) : undefined,
        role_in_project: newPlan.role || undefined,
        hourly_rate: newPlan.rate === "" ? 0 : Number(newPlan.rate),
        budgeted_hours: newPlan.hours === "" ? 0 : Number(newPlan.hours),
      });
      setIsPlanResOpen(false);
      setEditingPlanId(null);
      setNewPlan({ resource_kind: "STAFF", user_id: "", it_asset_id: "", role: "", rate: "", hours: "" });
      fetchPlan();
      setPlanToast({ type: "success", message: editingPlanId ? "Resource updated." : "Resource added." });
    } catch (err: any) {
      setPlanToast({ type: "error", message: err?.response?.data?.detail || "Failed to save resource" });
    }
  };

  const handleRemovePlanResource = async (rid: number): Promise<boolean> => {
    if (!(await swalConfirm("This resource will be removed from the project plan.", "Remove Resource?", "Yes, Remove"))) return false;
    try { await removeResource(project.id, rid); fetchPlan(); return true; }
    catch { swalError("Failed to remove resource"); return false; }
  };

  useEffect(() => {
    if (isEditTaskOpen && editingTask && editTab === "RESOURCES") {
      loadResources(editingTask.id);
    }
  }, [editTab, isEditTaskOpen, editingTask]);

  const handleAddResource = async () => {
    if (!editingTask || !newResource.resource_id) return;
    if (!vhEditTask.validateAll([
      { name: "newResource_quantity", code: 206, value: newResource.quantity },
    ])) return;

    // Check for duplicates
    const isDuplicate = resources.some(
      (r) =>
        r.resource_id === newResource.resource_id &&
        r.resource_type === newResource.resource_type,
    );

    if (isDuplicate) {
      swalError("This resource has already been added to the task.");
      return;
    }

    try {
      await taskService.createTaskResource(editingTask.id, newResource);
      loadResources(editingTask.id);
      setNewResource({
        ...newResource,
        resource_id: 0,
        quantity: 1,
        effort_hours: 0,
      });
      setIsAddResourceOpen(false);
      swalSuccess("Resource added!");
    } catch (error: any) {
      console.error("Failed to add resource:", error);
      swalError(`Failed to add resource: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteResource = async (resourceId: number) => {
    if (!(await swalConfirm("This resource will be removed from the task.", "Remove Resource?", "Yes, Remove"))) return;
    try {
      await taskService.deleteTaskResource(resourceId);
      if (editingTask) loadResources(editingTask.id);
    } catch (error) {
      console.error("Failed to delete resource:", error);
    }
  };

  // Add-Resource picker excludes anything already on the task, so a duplicate
  // can't be selected at all — the bad allocation is stopped at the source,
  // not on submit.
  const availableResourceOptions =
    newResource.resource_type === "STAFF"
      ? staffOptions
        .filter(
          (s) =>
            !resources.some(
              (r) =>
                (r.resource_type === "STAFF" || r.resource_type === "MEN") &&
                r.resource_id === s.user_id,
            ),
        )
        .map((s) => ({
          value: s.user_id,
          label: `${s.user_name}${s.hourly_rate ? ` - ${Number(s.hourly_rate).toFixed(2)}/hr` : ""}`,
        }))
      : taskMachineOptions
        .filter(
          (a) =>
            !resources.some(
              (r) =>
                r.resource_type === "MACHINE" && r.resource_id === a.it_asset_id,
            ),
        )
        .map((a) => ({
          value: a.it_asset_id,
          label: `${a.resource_name}${a.hourly_rate ? ` - ${Number(a.hourly_rate).toFixed(2)}/hr` : ""}`,
        }));

  // Full task payload from the working copy — shared by the General "Update"
  // and the self-contained constraint Save so neither wipes the other's fields.
  const buildTaskUpdateData = (t: Task): TaskUpdate => ({
    project_id: project.id,
    name: t.name,
    description: t.description,
    parent_id: t.parent_id,
    wbs_code: t.wbs_code,
    is_milestone: t.is_milestone,
    duration_planned: t.duration_planned,
    effort_estimated: t.effort_estimated,
    optimistic: t.optimistic,
    most_likely: t.duration_planned,
    pessimistic: t.pessimistic,
    constraint_type: t.constraint_type,
    constraint_date: t.constraint_type === "ASAP" ? null : t.constraint_date,
    deadline_date: t.deadline_date,
    actual_start_date: t.actual_start_date,
    actual_finish_date: t.actual_finish_date,
  });

  // Leave General edit mode without saving — restore the row from the schedule.
  const cancelGeneralEdit = () => {
    if (editingTask) {
      const orig =
        scheduledTasks.find((t) => t.id === editingTask.id) ||
        tasks.find((t) => t.id === editingTask.id);
      if (orig) setEditingTask(orig as Task);
    }
    setSwapToSeq("");
    vhEditTask.reset();
    setGeneralEditMode(false);
  };

  // The Scheduling Constraint saves itself (no modal-wide Update on this tab).
  const handleSaveConstraint = async () => {
    if (!editingTask) return;
    if (
      editingTask.constraint_type !== "ASAP" &&
      !vhEditTask.validateAll([
        { name: "edit_constraint_date", code: 305, value: editingTask.constraint_date, required: true },
      ])
    )
      return;
    try {
      await taskService.updateTask(editingTask.id, buildTaskUpdateData(editingTask));
      await loadData();
      swalSuccess("Constraint saved.");
    } catch (error: any) {
      swalError(`Failed to save constraint: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    const aStart = editingTask.actual_start_date || "";
    const aFinish = editingTask.actual_finish_date || "";
    const specs: any[] = [
      { name: "edit_name", code: 109, value: editingTask.name, required: true },
      { name: "edit_description", code: 110, value: editingTask.description },
      { name: "edit_duration_planned", code: 206, value: editingTask.duration_planned },
      { name: "edit_constraint_date", code: 305, value: editingTask.constraint_date },
      { name: "edit_actual_start_date", code: 305, value: aStart },
      { name: "edit_actual_finish_date", code: 305, value: aFinish },
    ];
    if (aStart && aFinish) {
      specs.push({
        name: "edit_actual_finish_date", code: 601, value: "x", required: true,
        additional: { from_date: aStart, to_date: aFinish },
      });
    }
    if (!vhEditTask.validateAll(specs)) return;
    try {
      const updateData: TaskUpdate = buildTaskUpdateData(editingTask);

      // If the user requested a sibling swap, do it first so the resulting
      // sequence_no is reflected in the row returned by loadData() below.
      const swapTarget = swapToSeq.trim();
      if (swapTarget !== "") {
        const tgt = parseInt(swapTarget, 10);
        if (!Number.isInteger(tgt) || tgt <= 0) {
          alert("Swap to Sequence ID must be a positive integer.");
          return;
        }
        if (tgt !== editingTask.sequence_no) {
          try {
            await taskService.swapTaskSequence(project.id, editingTask.id, tgt);
          } catch (swapError: any) {
            alert(
              `Swap failed: ${swapError.response?.data?.detail || swapError.message}`,
            );
            return;
          }
        }
      }

      // Auto-save pending dependency if selected but not added
      if (newDependency.predecessor_id) {
        try {
          await taskService.createDependency(project.id, {
            predecessor_id: parseInt(newDependency.predecessor_id), // Convert to int
            successor_id: editingTask.id,
            type: newDependency.type as any,
            lag_days: newDependency.lag_days,
          });
          setNewDependency({ predecessor_id: "", type: "FS", lag_days: 0 });
        } catch (depError: any) {
          console.error("Failed to auto-save dependency:", depError);
          alert(
            `Warning: Task updated, but failed to auto-save dependency: ${depError.response?.data?.detail || depError.message}`,
          );
        }
      }

      await taskService.updateTask(editingTask.id, updateData);
      setGeneralEditMode(false);
      setIsEditTaskOpen(false);
      loadData();
      swalSuccess("Task updated successfully!");
    } catch (error) {
      console.error("Failed to update task:", error);
      swalError("Failed to update task.");
    }
  };

  const handleAddDependency = async () => {
    if (!editingTask || !newDependency.predecessor_id) return;
    if (!vhEditTask.validateAll([
      { name: "lag_days", code: 201, value: newDependency.lag_days },
    ])) return;
    try {
      await taskService.createDependency(project.id, {
        predecessor_id: parseInt(newDependency.predecessor_id), // Convert to int
        successor_id: editingTask.id,
        type: newDependency.type as any,
        lag_days: newDependency.lag_days,
      });
      setNewDependency({ predecessor_id: "", type: "FS", lag_days: 0 });
      setIsAddPredOpen(false);
      loadData();
      swalSuccess("Predecessor added!");
    } catch (error: any) {
      console.error("Failed to add dependency:", error);
      swalError(
        `Failed to add predecessor: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this task? This action cannot be undone.",
      )
    )
      return;
    try {
      await taskService.deleteTask(project.id, taskId);
      loadData();
      window.alert("Task deleted successfully.");
    } catch (error: any) {
      console.error("Failed to delete task:", error);
      window.alert(
        `Failed to delete task: ${error.response?.data?.detail || error.message}`,
      );
    }
  };

  const handleDeleteDependency = async (dependencyId: number) => {
    if (!window.confirm("Are you sure you want to remove this dependency?"))
      return;
    try {
      await taskService.deleteDependency(project.id, dependencyId);
      loadData();
      window.alert("Dependency removed.");
    } catch (error) {
      console.error("Failed to remove dependency:", error);
      window.alert("Failed to remove dependency.");
    }
  };

  // Reachable set: tasks that can be walked from a real root (parent_id == null).
  // Anything not in this set is an "orphan" (cycled, dangling, or cross-project parent_id).
  // We compute this on every render against the latest scheduledTasks; cheap at our scale.
  const reachableSet = React.useMemo(() => {
    const reachable = new Set<number>();
    const queue: number[] = scheduledTasks
      .filter((t) => t.parent_id == null)
      .map((t) => t.id);
    while (queue.length) {
      const id = queue.shift()!;
      if (reachable.has(id)) continue;
      reachable.add(id);
      scheduledTasks
        .filter((t) => t.parent_id === id)
        .forEach((c) => queue.push(c.id));
    }
    return reachable;
  }, [scheduledTasks]);

  const isOrphan = (taskId: number) => !reachableSet.has(taskId);

  // Orphan-aware children. Calling with null returns true roots PLUS orphan tasks
  // (so they always appear in any tree-walking renderer).
  const getChildTasks = (parentId: number | null) => {
    if (parentId == null) {
      return scheduledTasks.filter(
        (t) => t.parent_id == null || isOrphan(t.id),
      );
    }
    // For non-root parents, only return children that are reachable through this branch.
    // Orphans are handled at root level — never recurse into them through their (broken) parent_id.
    return scheduledTasks.filter(
      (t) => t.parent_id === parentId && reachableSet.has(t.id),
    );
  };

  // --- RENDERERS ---

  const [hoveredTask, setHoveredTask] = useState<any>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [collapsedTaskIds, setCollapsedTaskIds] = useState<Set<number>>(new Set());
  const [swapToSeq, setSwapToSeq] = useState<string>("");

  const toggleTaskCollapse = (taskId: number) => {
    setCollapsedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const renderGantt = () => {
    const ROW_HEIGHT = 44;
    const DAY_WIDTH = 36;
    const LEFT_PANEL_WIDTH = 340;
    const HEADER_MONTH_H = 26;
    const HEADER_DAY_H = 26;

    const visibleTasks: any[] = [];
    const traverse = (pid: number | null) => {
      getChildTasks(pid).forEach((t) => {
        visibleTasks.push(t);
        if (!collapsedTaskIds.has(t.id)) traverse(t.id);
      });
    };
    traverse(null);

    const projectDuration = 90;
    const startDate = project.baseline_start_date || new Date().toISOString().split("T")[0];
    const days = Array.from({ length: projectDuration }, (_, i) => addDays(startDate, i));

    const todayStr = new Date().toISOString().split("T")[0];
    const todayOffset = diffDays(startDate, todayStr);

    // Month grouping
    const monthGroups: { label: string; count: number }[] = [];
    days.forEach((d: any) => {
      const label = new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" });
      const last = monthGroups[monthGroups.length - 1];
      if (last && last.label === label) last.count++;
      else monthGroups.push({ label, count: 1 });
    });

    const isWeekend = (d: any) => { const w = new Date(d + "T00:00:00").getDay(); return w === 0 || w === 6; };

    const getDepth = (task: any): number => {
      if (!task.parent_id) return 0;
      const p = scheduledTasks.find((t) => t.id === task.parent_id);
      return p ? getDepth(p) + 1 : 0;
    };

    return (
      <div
        className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        style={{ height: "calc(100vh - 170px)", minHeight: "calc(100vh - 170px)" }}
      >
        {/* Tooltip */}
        {hoveredTask && (
          <div
            className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl pointer-events-none"
            style={{ top: tooltipPos.y + 12, left: tooltipPos.x + 12, minWidth: 190 }}
          >
            <div className="font-semibold text-sm mb-2 border-b border-gray-700 pb-1.5">{hoveredTask.name}</div>
            <div className="space-y-1.5">
              {[
                ["Duration", `${hoveredTask.duration_planned}d`],
                ["Early Start", formatCPMDate(hoveredTask.early_start)],
                ["Early Finish", formatCPMDate(hoveredTask.early_finish)],
                ["Late Start", formatCPMDate(hoveredTask.late_start)],
                ["Late Finish", formatCPMDate(hoveredTask.late_finish)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6">
                  <span className="text-gray-400">{k}</span>
                  <span className="">{v}</span>
                </div>
              ))}
              {hoveredTask.total_slack !== undefined && (
                <div className="flex justify-between gap-6 pt-1 border-t border-gray-700">
                  <span className="text-gray-400">Float</span>
                  <span className={`font-bold ${hoveredTask.total_slack <= 0 ? "text-red-400" : "text-green-400"}`}>
                    {hoveredTask.total_slack}d
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toolbar / Legend */}
        <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-100 bg-gray-50 shrink-0">
          <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Gantt Chart</span>
          <div className="ml-auto flex items-center gap-5 text-[11px] text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-2.5 rounded-sm inline-block" style={{ background: "#7c3aed" }} />
              Normal
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-2.5 rounded-sm inline-block" style={{ background: "#ef4444" }} />
              Critical Path
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-px h-3 inline-block align-middle" style={{ background: "#f97316" }} />
              Today
            </span>
          </div>
        </div>

        {/* Scrollable area */}
        <div className="flex-1 overflow-auto relative">
          <div style={{ minWidth: `${LEFT_PANEL_WIDTH + days.length * DAY_WIDTH}px`, position: "relative" }}>

            {/* Month header row */}
            <div className="flex sticky top-0 z-20 border-b border-gray-200">
              <div
                className="shrink-0 sticky left-0 z-30 bg-gray-50 border-r border-gray-200 flex items-center px-4"
                style={{ width: LEFT_PANEL_WIDTH, height: HEADER_MONTH_H }}
              >
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Task</span>
                <span className="ml-auto text-[10px] font-bold text-gray-400 uppercase tracking-widest">Dur</span>
              </div>
              {monthGroups.map((grp, i) => (
                <div
                  key={i}
                  className="shrink-0 border-r border-gray-200 flex items-center px-2 bg-gray-50"
                  style={{ width: grp.count * DAY_WIDTH, height: HEADER_MONTH_H }}
                >
                  <span className="text-[11px] font-bold text-gray-600 truncate">{grp.label}</span>
                </div>
              ))}
            </div>

            {/* Day header row */}
            <div className="flex sticky z-20 border-b border-gray-200" style={{ top: HEADER_MONTH_H }}>
              <div
                className="shrink-0 sticky left-0 z-30 bg-white border-r border-gray-200"
                style={{ width: LEFT_PANEL_WIDTH, height: HEADER_DAY_H }}
              />
              {days.map((d: any, i) => {
                const isToday = d === todayStr;
                const weekend = isWeekend(d);
                return (
                  <div
                    key={i}
                    className={`shrink-0 border-r border-gray-100 flex items-center justify-center ${weekend ? "bg-gray-50" : "bg-white"} ${isToday ? "bg-orange-50" : ""}`}
                    style={{ width: DAY_WIDTH, height: HEADER_DAY_H }}
                  >
                    <span className={`text-[10px] font-medium ${isToday ? "text-orange-500 font-bold" : weekend ? "text-gray-300" : "text-gray-400"}`}>
                      {parseInt(d.split("-")[2])}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Rows */}
            <div className="relative">
              {/* Background grid + weekend shading + today line */}
              <div
                className="absolute inset-0 pointer-events-none flex"
                style={{ marginLeft: LEFT_PANEL_WIDTH }}
              >
                {days.map((d: any, i) => (
                  <div
                    key={i}
                    className={`shrink-0 border-r border-gray-100 h-full ${isWeekend(d) ? "bg-gray-50/70" : ""}`}
                    style={{ width: DAY_WIDTH }}
                  />
                ))}
                {todayOffset >= 0 && todayOffset < projectDuration && (
                  <div
                    className="absolute top-0 bottom-0"
                    style={{ left: todayOffset * DAY_WIDTH + DAY_WIDTH / 2 - 1, width: 2, background: "rgba(249,115,22,0.7)", zIndex: 5 }}
                  />
                )}
              </div>

              {visibleTasks.map((task, rowIdx) => {
                const depth = getDepth(task);
                const isParent = getChildTasks(task.id).length > 0;
                const isCritical = task.is_critical;
                const startDiff = diffDays(startDate, task.early_start);
                const barLeft = startDiff * DAY_WIDTH;
                const barWidth = Math.max(task.duration_planned * DAY_WIDTH, 6);
                const pct = Math.min(Math.max(task.percent_complete || 0, 0), 100);
                const mainColor = isCritical ? "#ef4444" : "#7c3aed";
                const lightColor = isCritical ? "#fecaca" : "#ddd6fe";
                const progressColor = isCritical ? "#b91c1c" : "#5b21b6";

                return (
                  <div
                    key={task.id}
                    className={`flex border-b border-gray-100 group transition-colors ${rowIdx % 2 === 1 ? "bg-slate-50/40" : ""} hover:bg-violet-50/30`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    {/* Left panel */}
                    <div
                      className="shrink-0 sticky left-0 z-10 border-r border-gray-200 flex items-center gap-1 pr-2 transition-colors bg-white group-hover:bg-violet-50/50"
                      style={{ width: LEFT_PANEL_WIDTH }}
                    >
                      <div
                        className="flex items-center flex-1 min-w-0 cursor-pointer"
                        style={{ paddingLeft: `${14 + depth * 22}px` }}
                        onClick={() => openEditModal(task)}
                        title="Open task"
                      >
                        {isParent && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleTaskCollapse(task.id); }}
                            className="p-0.5 -ml-0.5 mr-0.5 rounded hover:bg-gray-200 text-gray-500 shrink-0"
                            title={collapsedTaskIds.has(task.id) ? "Expand" : "Collapse"}
                          >
                            {collapsedTaskIds.has(task.id)
                              ? <ChevronRight className="w-3 h-3" />
                              : <ChevronDown className="w-3 h-3" />}
                          </button>
                        )}
                        {task.sequence_no != null && (
                          <span
                            className="shrink-0 mr-1.5 px-1 text-[10px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded"
                            title="Sequence ID (per parent)"
                          >
                            {task.sequence_no}
                          </span>
                        )}
                        {task.is_milestone && <span className="text-yellow-500 mr-1 text-xs">◆</span>}
                        <span
                          className={`text-[13px] truncate leading-tight ${isParent ? "font-semibold text-gray-800" : "text-gray-600"} ${isCritical ? "!text-red-700" : ""}`}
                        >
                          {task.name}
                        </span>
                      </div>
                      <span className="shrink-0 text-[10px] text-gray-400 w-8 text-right">
                        {task.duration_planned}d
                      </span>
                    </div>

                    {/* Bar area */}
                    <div className="relative flex-1 overflow-hidden">
                      {/* Float band */}
                      {task.early_start && task.late_finish && !isCritical && (
                        <div
                          className="absolute rounded-full opacity-25"
                          style={{
                            top: "50%", transform: "translateY(-50%)", height: 10,
                            left: diffDays(startDate, task.early_start) * DAY_WIDTH,
                            width: Math.max(diffDays(task.early_start, task.late_finish) * DAY_WIDTH, 0),
                            background: lightColor,
                          }}
                        />
                      )}

                      {/* Task bar */}
                      {task.early_start && (
                        task.is_milestone ? (
                          <div
                            className="absolute cursor-pointer"
                            style={{
                              top: "50%", transform: "translate(-50%, -50%) rotate(45deg)",
                              left: barLeft + DAY_WIDTH / 2,
                              width: 12, height: 12, background: mainColor,
                              borderRadius: 2, zIndex: 10,
                            }}
                            onMouseEnter={(e) => { setHoveredTask(task); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                            onMouseLeave={() => setHoveredTask(null)}
                            onClick={() => openEditModal(task)}
                          />
                        ) : isParent ? (
                          <div
                            className="absolute cursor-pointer overflow-hidden"
                            style={{
                              top: "50%", transform: "translateY(-50%)", height: 14,
                              left: barLeft, width: barWidth,
                              background: mainColor, borderRadius: 3, zIndex: 10,
                            }}
                            onMouseEnter={(e) => { setHoveredTask(task); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                            onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHoveredTask(null)}
                            onClick={() => openEditModal(task)}
                          >
                            {pct > 0 && (
                              <div style={{ width: `${pct}%`, height: "100%", background: progressColor, opacity: 0.7 }} />
                            )}
                          </div>
                        ) : (
                          <div
                            className="absolute cursor-pointer overflow-hidden"
                            style={{
                              top: "50%", transform: "translateY(-50%)", height: 14,
                              left: barLeft, width: barWidth,
                              background: lightColor, borderRadius: 99, zIndex: 10,
                              boxShadow: `0 1px 3px rgba(0,0,0,0.12)`,
                            }}
                            onMouseEnter={(e) => { setHoveredTask(task); setTooltipPos({ x: e.clientX, y: e.clientY }); }}
                            onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                            onMouseLeave={() => setHoveredTask(null)}
                            onClick={() => openEditModal(task)}
                          >
                            <div style={{ width: pct > 0 ? `${pct}%` : "100%", height: "100%", background: mainColor, borderRadius: 99 }} />
                          </div>
                        )
                      )}

                      {/* % label */}
                      {task.early_start && pct > 0 && !task.is_milestone && (
                        <div
                          className="absolute text-[9px] font-bold text-white pointer-events-none select-none"
                          style={{ top: "50%", transform: "translateY(-50%)", left: barLeft + 4, zIndex: 11 }}
                        >
                          {pct > 20 ? `${pct}%` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {visibleTasks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Calendar className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-sm">No tasks yet. Click + to add a task.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatCPMDate = (dateStr: string | undefined) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
        })
        .replace(/ /g, "-");
    } catch (e) {
      return dateStr;
    }
  };

  const renderCPM = () => {
    // Show all tasks, not just leaf tasks, as requested
    const tasksToRender = scheduledTasks;
    const LEVEL_WIDTH = 290;
    const ROW_HEIGHT = 170;

    // --- CPM Layout Algorithm ---
    const calculateCPMLayout = () => {
      const nodes = tasksToRender.map((t) => ({ ...t, level: 0, order: 0 }));
      const nodeMap = new Map(nodes.map((n) => [n.id, n]));
      const adj: Record<number, number[]> = {};
      const revAdj: Record<number, number[]> = {};

      // Initialize adjacency
      nodes.forEach((n) => {
        adj[n.id] = [];
        revAdj[n.id] = [];
      });

      dependencies.forEach((dep) => {
        if (adj[dep.predecessor_id] && adj[dep.successor_id]) {
          adj[dep.predecessor_id].push(dep.successor_id);
          revAdj[dep.successor_id].push(dep.predecessor_id);
        }
      });

      // 1. Calculate Levels (Longest Path / Topological Layering)
      // Initialize with 0. For each node, level = max(pred.level) + 1
      // Simple relaxation for DAG
      let changed = true;
      let iterations = 0;
      while (changed && iterations < nodes.length + 2) {
        changed = false;
        nodes.forEach((node) => {
          const preds = revAdj[node.id];
          if (preds.length > 0) {
            const maxPredLevel = Math.max(
              ...preds.map((pId) => nodeMap.get(pId)?.level || 0),
            );
            if (node.level < maxPredLevel + 1) {
              node.level = maxPredLevel + 1;
              changed = true;
            }
          }
        });
        iterations++;
      }

      // 2. Group by Level
      const levels: Record<number, typeof nodes> = {};
      nodes.forEach((n) => {
        if (!levels[n.level]) levels[n.level] = [];
        levels[n.level].push(n);
      });

      // 3. Ordering (Barycenter Heuristic) to minimize crossings
      const maxLevel = Math.max(...nodes.map((n) => n.level));

      // Initial sort by ID to be deterministic
      Object.values(levels).forEach((lvlNodes) =>
        lvlNodes.sort((a, b) => a.id - b.id),
      );

      // Forward pass (barycenter)
      for (let l = 1; l <= maxLevel; l++) {
        const currentLevelNodes = levels[l];
        if (!currentLevelNodes) continue;

        currentLevelNodes.forEach((node) => {
          const preds = revAdj[node.id];
          if (preds.length > 0) {
            const sumY = preds.reduce((sum, pId) => {
              const predNode = nodeMap.get(pId);
              // Find index of predNode in its level
              const predIndex = levels[l - 1]?.indexOf(predNode as any) || 0;
              return sum + predIndex;
            }, 0);
            (node as any).barycenter = sumY / preds.length;
          } else {
            (node as any).barycenter = (node as any).order; // Keep relative
          }
        });

        currentLevelNodes.sort((a: any, b: any) => a.barycenter - b.barycenter);
      }

      // Assign Coordinates
      const layoutCoords: Record<number, { x: number; y: number }> = {};
      Object.keys(levels).forEach((lvlKey) => {
        const lvl = parseInt(lvlKey);
        levels[lvl].forEach((node, idx) => {
          layoutCoords[node.id] = {
            x: 50 + lvl * LEVEL_WIDTH,
            y: 50 + idx * ROW_HEIGHT,
          };
        });
      });

      return layoutCoords;
    };

    const coords = calculateCPMLayout();

    const renderConnections = () => {
      const paths: any[] = [];

      dependencies.forEach((dep) => {
        // Only draw if both tasks are visible in the current view
        if (coords[dep.predecessor_id] && coords[dep.successor_id]) {
          const start = coords[dep.predecessor_id];
          const end = coords[dep.successor_id];

          const predTask = scheduledTasks.find(
            (t) => t.id === dep.predecessor_id,
          );
          const succTask = scheduledTasks.find(
            (t) => t.id === dep.successor_id,
          );

          const isLinkCritical = predTask?.is_critical && succTask?.is_critical;

          // Bezier curve for smooth connection
          const pathData = `M ${start.x + 240} ${start.y + 57} C ${start.x + 265} ${start.y + 57}, ${end.x - 25} ${end.y + 57}, ${end.x} ${end.y + 57}`;

          paths.push(
            <g key={dep.id}>
              <path
                d={pathData}
                fill="none"
                stroke={isLinkCritical ? "#ef4444" : "#cbd5e1"}
                strokeWidth={isLinkCritical ? 3 : 1.5}
                markerEnd={
                  isLinkCritical
                    ? "url(#arrowhead-critical)"
                    : "url(#arrowhead)"
                }
              />
            </g>,
          );
        }
      });
      return paths;
    };

    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-100 bg-gray-50 shrink-0">
          <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">CPM Network</span>
          <div className="ml-auto flex items-center gap-5 text-[11px] text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#818cf8', border: '1.5px solid #818cf8' }} />
              Normal
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#ef4444', border: '2px solid #ef4444' }} />
              Critical Path
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#16a34a' }} />
              Float &gt; 0
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#dc2626' }} />
              Float = 0
            </span>
          </div>
        </div>
        {/* Scrollable canvas */}
        <div className="flex-1 overflow-auto relative" style={{ background: '#f8fafc' }}>
          <svg className="w-[2000px] h-[1000px]">
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#cbd5e1" />
              </marker>
              <marker
                id="arrowhead-critical"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
              </marker>
            </defs>
            {renderConnections()}
            {tasksToRender.map((task) => {
              const pos = coords[task.id];
              if (!pos) return null;
              // Node geometry
              // Rows: TOP 0-36 | MID 36-79 | BOT 79-115
              // Cols (top+bot): LEFT 0-80 | CTR 80-160 | RIGHT 160-240
              const W = 240, H = 115;
              const isCrit = task.is_critical;
              const accent = isCrit ? '#dc2626' : '#6366f1';
              const rowBg = isCrit ? '#fff1f2' : '#eef2ff';
              const border = isCrit ? '#ef4444' : '#818cf8';
              const div = isCrit ? '#fecaca' : '#c7d2fe';
              const lbl = isCrit ? '#b91c1c' : '#4338ca';
              const val = '#111827';
              const nameFill = isCrit ? '#7f1d1d' : '#312e81';
              const slackFill = task.total_slack <= 0 ? '#dc2626' : '#16a34a';
              const shadow = isCrit ? 'rgba(220,38,38,0.14)' : 'rgba(99,102,241,0.12)';
              const esMs = task.early_start ? new Date(task.early_start).getTime() : 0;
              const efMs = task.early_finish ? new Date(task.early_finish).getTime() : 0;
              const dur = esMs && efMs ? Math.round((efMs - esMs) / 86400000) + 1 : '-';
              const label = task.name.length > 25 ? task.name.slice(0, 24) + '…' : task.name;
              return (
                <g key={task.id} transform={`translate(${pos.x}, ${pos.y})`} style={{ cursor: 'pointer' }} onClick={() => openEditModal(task)}>
                  {/* shadow */}
                  <rect x="3" y="5" width={W} height={H} rx="10" fill={shadow} />
                  {/* card */}
                  <rect x="0" y="0" width={W} height={H} rx="10" fill="white" stroke={border} strokeWidth={isCrit ? 2.5 : 1.5} />

                  {/* top row bg — rounded top, square bottom */}
                  <rect x="1" y="1" width={W - 2} height="36" rx="9" fill={rowBg} />
                  <rect x="1" y="22" width={W - 2} height="15" fill={rowBg} />

                  {/* bottom row bg — square top, rounded bottom */}
                  <rect x="1" y="79" width={W - 2} height="35" rx="9" fill={rowBg} />
                  <rect x="1" y="79" width={W - 2} height="15" fill={rowBg} />

                  {/* dividers */}
                  <line x1="1" y1="36" x2={W - 1} y2="36" stroke={div} strokeWidth="1" />
                  <line x1="1" y1="79" x2={W - 1} y2="79" stroke={div} strokeWidth="1" />
                  <line x1="80" y1="1" x2="80" y2="36" stroke={div} strokeWidth="1" />
                  <line x1="160" y1="1" x2="160" y2="36" stroke={div} strokeWidth="1" />
                  <line x1="80" y1="79" x2="80" y2={H - 1} stroke={div} strokeWidth="1" />
                  <line x1="160" y1="79" x2="160" y2={H - 1} stroke={div} strokeWidth="1" />

                  {/* TOP ROW — ES | DUR | EF */}
                  <text x="8" y="13" fill={lbl} fontSize="8" fontWeight="700" letterSpacing="1">ES</text>
                  <text x="40" y="29" fill={val} fontSize="10" fontWeight="600" textAnchor="middle">{formatCPMDate(task.early_start)}</text>

                  <text x="120" y="13" fill={lbl} fontSize="8" fontWeight="700" textAnchor="middle" letterSpacing="1">DUR</text>
                  <text x="120" y="30" fill={accent} fontSize="14" fontWeight="800" textAnchor="middle">{dur}d</text>

                  <text x="232" y="13" fill={lbl} fontSize="8" fontWeight="700" textAnchor="end" letterSpacing="1">EF</text>
                  <text x="200" y="29" fill={val} fontSize="10" fontWeight="600" textAnchor="middle">{formatCPMDate(task.early_finish)}</text>

                  {/* MIDDLE ROW — Task Name */}
                  <text x="120" y="62" fill={nameFill} fontSize="11" fontWeight="700" textAnchor="middle">{label}</text>

                  {/* BOTTOM ROW — LS | FLOAT | LF */}
                  <text x="8" y="92" fill={lbl} fontSize="8" fontWeight="700" letterSpacing="1">LS</text>
                  <text x="40" y="110" fill={val} fontSize="10" fontWeight="600" textAnchor="middle">{formatCPMDate(task.late_start)}</text>

                  <text x="120" y="92" fill="#6b7280" fontSize="8" fontWeight="700" textAnchor="middle" letterSpacing="1">FLOAT</text>
                  <rect x="104" y="95" width="32" height="18" rx="5" fill={slackFill} />
                  <text x="120" y="108" fill="white" fontSize="11" fontWeight="700" textAnchor="middle">{task.total_slack}</text>

                  <text x="232" y="92" fill={lbl} fontSize="8" fontWeight="700" textAnchor="end" letterSpacing="1">LF</text>
                  <text x="200" y="110" fill={val} fontSize="10" fontWeight="600" textAnchor="middle">{formatCPMDate(task.late_finish)}</text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  // Compute the set of tasks reachable from a real root (parent_id == null).
  // Anything not reachable is an orphan (cycle or dangling parent_id) and must
  // still be visible to the user. We render reachable as a tree, then orphans flat.
  // Returns the set of descendant task IDs of the given task (used to exclude
  // them from the parent picker so users can't create cycles).
  const getDescendantIds = (taskId: number): Set<number> => {
    const out = new Set<number>();
    const queue: number[] = [taskId];
    while (queue.length) {
      const id = queue.shift()!;
      tasks
        .filter((t) => t.parent_id === id)
        .forEach((c) => {
          if (!out.has(c.id)) {
            out.add(c.id);
            queue.push(c.id);
          }
        });
    }
    return out;
  };

  const renderWBSRows = (parentId: number | null = null, level = 0): any => {
    const children = getChildTasks(parentId);
    return children.map((task) => {
      const orphan = isOrphan(task.id);
      return (
        <React.Fragment key={task.id}>
          <tr
            className={`hover:bg-gray-50 border-b border-gray-100 group ${task.is_critical ? "bg-red-50/20" : ""} ${orphan ? "bg-amber-50/40" : ""}`}
          >
            <td className="p-3 pl-4">
              <div
                className="flex items-center"
                style={{ paddingLeft: `${level * 20}px` }}
              >
                {getChildTasks(task.id).length > 0 && (
                  <ChevronDown className="w-4 h-4 mr-2 text-gray-400" />
                )}
                <span
                  className={`text-sm ${level === 0 ? "font-bold text-gray-800" : "text-gray-600"} mr-2 cursor-pointer hover:underline`}
                  onClick={() => openEditModal(task)}
                  title="Open task"
                >
                  {task.name}
                </span>
                {orphan && (
                  <span
                    className="status-badge xs danger mr-2"
                    title="Parent is missing or in a cycle. Edit to re-parent."
                  >
                    orphan
                  </span>
                )}
              </div>
            </td>
            <td className="p-3 text-sm text-center text-gray-600">
              {task.duration_planned}d
            </td>
            <td className="p-3 text-sm text-center text-gray-600">
              {task.early_start}
            </td>
            <td className="p-3 text-sm text-center text-gray-600">
              {task.early_finish}
            </td>
            <td className="p-3 text-sm text-center">
              {task.constraint_type !== "ASAP" && (
                <span className="status-badge xs warning">
                  {task.constraint_type}
                </span>
              )}
            </td>
          </tr>
          {renderWBSRows(task.id, level + 1)}
        </React.Fragment>
      );
    });
  };

  const renderResourcePlan = () => {
    const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const cost = (r: ProjectResource) => Number(r.budgeted_hours || 0) * Number(r.hourly_rate || 0);
    const total = planRows.reduce((s, r) => s + cost(r), 0);
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-gray-800">Resource Plan</div>
            <div className="text-xs text-gray-500">High-level mapping of men &amp; machines and their budgeted hours (project level, independent of tasks).</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Total Budgeted</div>
              <div className="text-base font-bold" style={{ color: '#4d288b' }}>{fmt(total)}</div>
            </div>
            <Button variant="fill" icon={Plus} type="button" onClick={openAddPlan} data-testid="BTN_pm_plan_add_open">Create</Button>
          </div>
        </div>
        <div className="table-list" style={{ display: "flex", flexDirection: "column" }}>
          <div
            className="table-list-scroll"
            style={!planLoading && planRows.length === 0 ? { flex: "0 0 auto" } : undefined}
          >
            <table className="table-list-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th className="table-list-th">Resource</th>
                  <th className="table-list-th">Role</th>
                  <th className="table-list-th table-list-cell--right">Rate / hr</th>
                  <th className="table-list-th table-list-cell--right">Budgeted Hrs</th>
                  <th className="table-list-th table-list-cell--right">Budgeted Cost</th>
                  <th className="table-list-th table-list-cell--priority">Type</th>
                </tr>
              </thead>
              <tbody aria-busy={planLoading} aria-live="polite">
                {planLoading && (
                  <Skeleton.TableRows
                    cols={['2xs', 'md', 'sm', 'sm', 'sm', 'sm', 'pill']}
                  />
                )}
                {!planLoading && planRows.map((r, idx) => (
                  <tr key={r.id} className="table-list-row" onClick={() => openEditPlan(r)}>
                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-title">{r.resource_name || r.user_name || '—'}</span>
                    </td>
                    <td className="table-list-cell">{r.role_in_project || '—'}</td>
                    <td className="table-list-cell table-list-cell--right">{fmt(Number(r.hourly_rate || 0))}</td>
                    <td className="table-list-cell table-list-cell--right">{Number(r.budgeted_hours || 0)}</td>
                    <td className="table-list-cell table-list-cell--right">{fmt(cost(r))}</td>
                    <td className="table-list-cell table-list-cell--priority">
                      {r.resource_kind === 'MACHINE' ? 'Machine' : 'Man'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!planLoading && planRows.length === 0 && (
            <NoDataFound
              title="No resources mapped yet"
              message='Use "Add Resource" to map men and machines to this project.'
            />
          )}
        </div>
      </div>
    );
  };

  const renderVariance = () => {
    const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const sanctioned = Number(project.budget_amount ?? 0);
    const totalBudget = scheduledTasks.reduce((s, t) => s + (t.budgeted_resource_cost ?? 0), 0);
    const totalActual = scheduledTasks.reduce((s, t) => s + (t.actual_resource_cost ?? 0), 0);
    const baseCost = sanctioned > 0 ? sanctioned : totalBudget;
    const costVariance = baseCost - totalActual;
    // Schedule overrun: project planned finish vs projected (actual-or-planned) finish.
    const maxMs = (pick: (t: any) => string | null | undefined) =>
      scheduledTasks.reduce((m, t) => {
        const ref = pick(t);
        const d = ref ? new Date(ref).getTime() : 0;
        return d > m ? d : m;
      }, 0);
    const planFinishMs = maxMs((t) => t.early_finish);
    const projFinishMs = maxMs((t) => t.actual_finish_date || t.early_finish);
    const scheduleVarianceDays =
      planFinishMs && projFinishMs ? Math.round((projFinishMs - planFinishMs) / 86400000) : 0;
    return (
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-800">Variance Analysis</h3>
          <div className="flex gap-2 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span> On
              Track
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500"></span> Delayed
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>{" "}
              Upcoming
            </span>
          </div>
        </div>

        {/* Overall cost & schedule overrun */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: sanctioned > 0 ? "Project Budget" : "Allocated (Tasks)", value: fmt(baseCost), color: "#4d288b" },
            { label: "Actual (Booked)", value: fmt(totalActual), color: totalActual > baseCost ? "#dc2626" : "#16a34a" },
            { label: "Cost Variance", value: fmt(costVariance), color: costVariance >= 0 ? "#16a34a" : "#dc2626" },
            { label: "Schedule Variance", value: `${scheduleVarianceDays > 0 ? "+" : ""}${scheduleVarianceDays} d`, color: scheduleVarianceDays > 0 ? "#dc2626" : "#16a34a" },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{c.label}</div>
              <div className="text-lg font-bold" style={{ color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-left border-collapse bg-white">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold tracking-wider">
                <th className="p-4 w-1/4">Task Details</th>
                <th className="p-4">Start Date</th>
                <th className="p-4">Finish Date</th>
                <th className="p-4">Time Variance</th>
                <th className="p-4">Cost Variance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {scheduledTasks
                .filter((t) => !t.parent_id || isOrphan(t.id))
                .flatMap((phase) => [
                  phase,
                  ...scheduledTasks.filter(
                    (c) => c.parent_id === phase.id && !isOrphan(c.id),
                  ),
                ])
                .map((task) => {
                  const plannedStart = task.early_start;
                  const actualStart = task.actual_start_date;
                  const plannedFinish = task.early_finish;
                  const actualFinish = task.actual_finish_date;

                  // Cost data
                  const budgetedCost = task.budgeted_resource_cost || 0;
                  const actualCost = task.actual_resource_cost || 0;

                  // Variance Logic
                  const isStartDelayed =
                    actualStart &&
                    plannedStart &&
                    new Date(actualStart) > new Date(plannedStart);
                  const isFinishDelayed =
                    actualFinish &&
                    plannedFinish &&
                    new Date(actualFinish) > new Date(plannedFinish);
                  const isCostOverrun = actualCost > budgetedCost;

                  const isUpcoming = !actualStart;

                  // Time (schedule) variance in days — finish-based once done;
                  // while running, fall back to start slippage. Positive = late.
                  const dayMs = 86400000;
                  let timeDays: number | null = null;
                  if (actualFinish && plannedFinish) {
                    timeDays = Math.round((new Date(actualFinish).getTime() - new Date(plannedFinish).getTime()) / dayMs);
                  } else if (actualStart && plannedStart && !actualFinish) {
                    timeDays = Math.round((new Date(actualStart).getTime() - new Date(plannedStart).getTime()) / dayMs);
                  }
                  const timeRunning = !!actualStart && !actualFinish;

                  const getStatusColor = (
                    isDelayed: boolean,
                    isUpcoming: boolean,
                  ) => {
                    if (isUpcoming) return "bg-blue-50/50";
                    if (isDelayed) return "bg-red-50";
                    return "bg-green-50";
                  };

                  return (
                    <tr
                      key={task.id}
                      className="hover:bg-gray-50/80 transition-colors group"
                    >
                      <td className="p-4 align-top">
                        <div
                          className={`flex flex-col gap-1 ${task.parent_id ? "pl-6 border-l-2 border-gray-100 ml-2" : ""}`}
                        >
                          <span
                            className={`font-medium text-gray-900 cursor-pointer hover:underline ${!task.parent_id ? "text-base" : "text-sm"}`}
                            onClick={() => openEditModal(task)}
                            title="Open task"
                          >
                            {task.name}
                          </span>
                        </div>
                      </td>

                      <td
                        className={`p-4 align-top ${getStatusColor(!!isStartDelayed, isUpcoming)}`}
                      >
                        <div className="flex flex-col gap-1 text-sm">
                          <div className="flex justify-between items-center text-gray-500">
                            <span className="text-[10px] uppercase tracking-wide font-semibold">
                              Plan
                            </span>
                            <span className="">
                              {formatCPMDate(plannedStart)}
                            </span>
                          </div>
                          <div
                            className={`flex justify-between items-center ${isStartDelayed ? "text-red-600 font-medium" : "text-gray-700"}`}
                          >
                            <span className="text-[10px] uppercase tracking-wide font-semibold">
                              Act
                            </span>
                            <span className="">
                              {formatCPMDate(actualStart)}
                            </span>
                          </div>
                          {!isUpcoming && (
                            <div className="mt-1">
                              {isStartDelayed ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 uppercase tracking-wider">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>{" "}
                                  Delayed
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 uppercase tracking-wider">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{" "}
                                  On Time
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      <td
                        className={`p-4 align-top ${getStatusColor(!!isFinishDelayed, isUpcoming)}`}
                      >
                        <div className="flex flex-col gap-1 text-sm">
                          <div className="flex justify-between items-center text-gray-500">
                            <span className="text-[10px] uppercase tracking-wide font-semibold">
                              Plan
                            </span>
                            <span className="">
                              {formatCPMDate(plannedFinish)}
                            </span>
                          </div>
                          <div
                            className={`flex justify-between items-center ${isFinishDelayed ? "text-red-600 font-medium" : "text-gray-700"}`}
                          >
                            <span className="text-[10px] uppercase tracking-wide font-semibold">
                              Act
                            </span>
                            <span className="">
                              {formatCPMDate(actualFinish)}
                            </span>
                          </div>
                          {!isUpcoming && (
                            <div className="mt-1">
                              {isFinishDelayed ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 uppercase tracking-wider">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>{" "}
                                  Delayed
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 uppercase tracking-wider">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{" "}
                                  On Time
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      <td
                        className={`p-4 align-top ${isUpcoming ? "bg-blue-50/40" : timeDays != null && timeDays > 0 ? "bg-red-50" : "bg-green-50"}`}
                      >
                        {isUpcoming ? (
                          <span className="text-xs text-gray-400 italic">Not started</span>
                        ) : timeDays == null ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <span className={`text-xl font-bold leading-none ${timeDays > 0 ? "text-red-600" : "text-green-600"}`}>
                              {timeDays > 0 ? `+${timeDays}` : timeDays}
                              <span className="text-xs font-semibold ml-0.5">d</span>
                            </span>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${timeDays > 0 ? "text-red-600" : "text-green-600"}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${timeDays > 0 ? "bg-red-500" : "bg-green-500"}`}></span>
                              {timeDays > 0 ? "Delayed" : timeDays < 0 ? "Ahead" : "On Time"}
                              {timeRunning && timeDays !== 0 ? " · running" : ""}
                            </span>
                          </div>
                        )}
                      </td>

                      <td
                        className={`p-4 align-top ${isCostOverrun ? "bg-red-50" : "bg-green-50"}`}
                      >
                        <div className="flex flex-col gap-1 text-sm">
                          <div className="flex justify-between items-center text-gray-500">
                            <span className="text-[10px] uppercase tracking-wide font-semibold">
                              Bud
                            </span>
                            <span>${budgetedCost.toFixed(0)}</span>
                          </div>
                          <div
                            className={`flex justify-between items-center ${isCostOverrun && actualCost > 0 ? "text-red-600 font-medium" : "text-gray-700"}`}
                          >
                            <span className="text-[10px] uppercase tracking-wide font-semibold">
                              Act
                            </span>
                            <span>${actualCost.toFixed(0)}</span>
                          </div>
                          <div className="mt-1">
                            {isCostOverrun ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 uppercase tracking-wider">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>{" "}
                                Overrun
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 uppercase tracking-wider">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{" "}
                                On Budget
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Kanban — leaf tasks grouped by execution status (derived from actuals).
  const renderKanban = () => {
    const leaves = scheduledTasks.filter(
      (t) => !scheduledTasks.some((c) => c.parent_id === t.id) && !t.is_milestone,
    );
    const cols: { key: string; label: string; tint: string; test: (t: any) => boolean }[] = [
      { key: "UPCOMING", label: "Upcoming", tint: "#3b82f6", test: (t) => !t.actual_start_date },
      { key: "INPROGRESS", label: "In Progress", tint: "#f59e0b", test: (t) => !!t.actual_start_date && !t.actual_finish_date },
      { key: "DONE", label: "Completed", tint: "#16a34a", test: (t) => !!t.actual_finish_date },
    ];
    return (
      <div className="grid grid-cols-3 gap-4">
        {cols.map((col) => {
          const items = leaves.filter(col.test);
          return (
            <div key={col.key} className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: col.tint }}>{col.label}</span>
                <span className="text-xs font-semibold text-gray-400">{items.length}</span>
              </div>
              <div className="p-2 space-y-2 overflow-auto" style={{ maxHeight: "calc(100vh - 280px)" }}>
                {items.length === 0 ? (
                  <div className="text-center text-xs text-gray-300 italic py-6">None</div>
                ) : (
                  items.map((t) => {
                    const delayed =
                      (t.actual_start_date && t.late_start && new Date(t.actual_start_date) > new Date(t.late_start)) ||
                      (t.actual_finish_date && t.late_finish && new Date(t.actual_finish_date) > new Date(t.late_finish));
                    return (
                      <div
                        key={t.id}
                        onClick={() => openEditModal(t)}
                        className="bg-white rounded-md border border-gray-200 p-2.5 cursor-pointer hover:shadow-sm transition-shadow"
                        style={t.is_critical ? { borderLeft: "3px solid #ef4444" } : undefined}
                      >
                        <div className="text-sm font-medium text-gray-800 truncate">{t.name}</div>
                        <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                          <span>{formatCPMDate(t.early_start)} → {formatCPMDate(t.early_finish)}</span>
                          {delayed && <span className="text-red-500 font-semibold">Delayed</span>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="xl">
        <Modal.Header

        >
          {project.name} Details
        </Modal.Header>
        <Modal.Body >
          {/* Tab Navigation */}
          <div className="app-modal-tabs">
            {(canManage
              ? [
                  { id: "DETAILS", label: "Basic Details" },
                  { id: "RESOURCES", label: "Resources" },
                  { id: "WBS", label: "WBS" },
                  ...(extraTabs || []).map((t) => ({ id: t.id, label: t.label })),
                ]
              : [{ id: "WBS", label: "WBS" }]
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`app-modal-tab${activeTab === tab.id ? " app-modal-tab--active" : ""}`}
                data-testid={`TAB_pm_project_details_${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content Area */}
          <div className="flex-1 bg-gray-50/50  overflow-auto">
            {activeTab === "DETAILS" && (
              <div className="space-y-4">
                {!isEditing ? (
                  <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
                    <KV label="Project Name" value={editFormData.name} />
                    <KV label="Project Code" value={editFormData.project_code} />
                    <KV label="Estimated Start" value={editFormData.baseline_start_date ? formatCPMDate(editFormData.baseline_start_date) : null} />
                    <KV label="Estimated Finish" value={editFormData.baseline_finish_date ? formatCPMDate(editFormData.baseline_finish_date) : null} />
                    <KV label="Status" value={editFormData.status} />
                    <KV label="Project Lead" value={leadName(editFormData.project_manager_id) ?? "— unassigned —"} />
                    <KV label="Project Budget" value={editFormData.budget_amount != null ? `$${Number(editFormData.budget_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null} />
                    <KV label="Description" value={editFormData.description} full />
                  </dl>
                ) : (
              <form
                ref={editFormRef}
                onSubmit={handleSaveDetails}
                className="grid grid-cols-2 gap-x-6 gap-y-4"
                data-testid="MOD_pm_project_details_form"
              >
                <ValidatedInput
                  vh={vhDetails}
                  name="name"
                  code={109}
                  label="Project Name"
                  required
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  disabled={!isEditing}
                  data-testid="INP_pm_project_details_name"
                />
                <FormControl.Input
                  label="Project Code"
                  value={editFormData.project_code}
                  disabled
                  data-testid="INP_pm_project_details_code"
                />
                <ValidatedDatePicker
                  vh={vhDetails}
                  name="baseline_start_date"
                  code={305}
                  label="Start Date"
                  value={editFormData.baseline_start_date}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      baseline_start_date: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  data-testid="INP_pm_project_details_start_date"
                />
                <ValidatedDatePicker
                  vh={vhDetails}
                  name="baseline_finish_date"
                  code={305}
                  label="Finish Date"
                  value={editFormData.baseline_finish_date}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      baseline_finish_date: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  data-testid="INP_pm_project_details_finish_date"
                />
                <FormControl.SearchSelect
                  label="Status"
                  data-testid="SEL_pm_project_details_status"
                  options={[
                    { value: "DRAFT", label: "Draft" },
                    { value: "ACTIVE", label: "Active" },
                    { value: "ON_HOLD", label: "On Hold" },
                    { value: "COMPLETED", label: "Completed" },
                    { value: "CANCELLED", label: "Cancelled" },
                  ]}
                  value={editFormData.status}
                  onChange={(v) =>
                    setEditFormData({
                      ...editFormData,
                      status: String(v ?? "DRAFT") as any,
                    })
                  }
                  disabled={!isEditing}
                />
                <FormControl.SearchSelect
                  label="Project Lead (head)"
                  data-testid="SEL_pm_project_details_lead"
                  options={[
                    { value: "", label: "— Unassigned —" },
                    ...leadUsers.map((u: any) => ({
                      value: String(u.id),
                      label: `${u.first_name} ${u.last_name}`.trim(),
                    })),
                  ]}
                  value={editFormData.project_manager_id ? String(editFormData.project_manager_id) : null}
                  onChange={(v) =>
                    setEditFormData({
                      ...editFormData,
                      project_manager_id: v ? parseInt(String(v)) : undefined,
                    })
                  }
                  placeholder="— Unassigned —"
                  clearable
                  disabled={!isEditing}
                />
                <FormControl.Input
                  label="Project Budget (sanctioned)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editFormData.budget_amount ?? ""}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      budget_amount: e.target.value === "" ? undefined : parseFloat(e.target.value),
                    })
                  }
                  disabled={!isEditing}
                  data-testid="INP_pm_project_detail_budget"
                />
                <div className="col-span-2">
                  <ValidatedTextarea
                    vh={vhDetails}
                    name="description"
                    code={110}
                    label="Description"
                    value={editFormData.description}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      })
                    }
                    disabled={!isEditing}
                    data-testid="INP_pm_project_details_description"
                  />
                </div>
              </form>
                )}
              </div>
            )}
            {activeTab === "RESOURCES" && renderResourcePlan()}
            {activeTab === "WBS" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                    {((canManage
                      ? [["GANTT", "Gantt"], ["CPM", "CPM"], ["GRID", "WBS Grid"], ["KANBAN", "Kanban"], ["VARIANCE", "Variance"]]
                      : [["GRID", "WBS Grid"], ["KANBAN", "Kanban"]]
                    ) as [typeof wbsView, string][]).map(([v, label]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setWbsView(v)}
                        className={`px-3 py-1.5 font-medium transition-colors ${wbsView === v ? "bg-violet-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                        data-testid={`BTN_pm_wbs_view_${v}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {canManage && (
                    <Button
                      variant="fill"
                      icon={Plus}
                      type="button"
                      onClick={() => {
                        setNewTask({
                          project_id: project.id,
                          name: "",
                          description: "",
                          is_milestone: false,
                          duration_planned: 1,
                          constraint_type: "ASAP",
                          wbs_code: "",
                        });
                        vhNewTask.reset();
                        setIsCreateTaskOpen(true);
                      }}
                      data-testid="BTN_pm_task_create_open"
                    >
                      Create Task
                    </Button>
                  )}
                </div>
                {wbsView === "GANTT" && renderGantt()}
                {wbsView === "CPM" && renderCPM()}
                {wbsView === "KANBAN" && renderKanban()}
                {wbsView === "VARIANCE" && renderVariance()}
                {wbsView === "GRID" && (
                <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b text-xs uppercase text-gray-500 font-medium">
                      <th className="p-3 pl-4 w-1/3">Task Name</th>
                      <th className="p-3 text-center w-24">Duration</th>
                      <th className="p-3 text-center w-32">Start</th>
                      <th className="p-3 text-center w-32">Finish</th>
                      <th className="p-3 text-center w-32">Constraint</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {renderWBSRows()}
                  </tbody>
                </table>
                </div>
                )}
              </div>
            )}
            <Modal
              isOpen={isCreateTaskOpen}
              onClose={() => setIsCreateTaskOpen(false)}
              size="lg"
              data-testid="MOD_pm_task_create"
            >
              <Modal.Header>Create Task</Modal.Header>
              <Modal.Body>
              <form
                id="create-task-form"
                onSubmit={(e) => { e.preventDefault(); handleAddTask(); }}
                className="grid grid-cols-2 gap-x-6 gap-y-4"
                data-testid="MOD_pm_task_create_form"
              >
                <div className="col-span-2">
                  <ValidatedInput
                    vh={vhNewTask}
                    name="name"
                    code={109}
                    label="Task Name"
                    required
                    value={newTask.name}
                    onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                    placeholder="e.g., Requirements Gathering"
                    data-testid="INP_pm_task_create_name"
                  />
                </div>
                <div className="col-span-2">
                  <ValidatedTextarea
                    vh={vhNewTask}
                    name="description"
                    code={110}
                    label="Description"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Task details..."
                    rows={3}
                    data-testid="INP_pm_task_create_description"
                  />
                </div>
                <FormControl.SearchSelect
                  label="Parent Task"
                  data-testid="SEL_pm_task_create_parent"
                  options={[
                    { value: "", label: "-- No Parent (Root) --" },
                    ...tasks.map((t) => ({
                      value: String(t.id),
                      label: t.name + (isOrphan(t.id) ? " (orphan — fix first)" : ""),
                      disabled: isOrphan(t.id),
                    })),
                  ]}
                  value={newTask.parent_id ? String(newTask.parent_id) : null}
                  onChange={(v) => setNewTask({ ...newTask, parent_id: v ? parseInt(String(v)) : null })}
                  placeholder="-- No Parent (Root) --"
                  clearable
                />
                <ValidatedInput
                  vh={vhNewTask}
                  name="duration_planned"
                  code={206}
                  label="Duration (Days)"
                  type="number"
                  value={String(newTask.duration_planned)}
                  onChange={(e) => setNewTask({ ...newTask, duration_planned: parseFloat(e.target.value) })}
                  min="0"
                  disabled={newTask.is_milestone}
                  data-testid="INP_pm_task_create_duration"
                />
                <FormControl.Input
                  label="WBS Code (Optional)"
                  maxLength={50}
                  value={newTask.wbs_code || ""}
                  onChange={(e) => setNewTask({ ...newTask, wbs_code: e.target.value })}
                  placeholder="e.g., 1.1"
                  data-testid="INP_pm_task_create_wbs"
                />
                <ValidatedInput
                  vh={vhNewTask}
                  name="sequence_no"
                  code={202}
                  label="Sequence ID (Optional)"
                  type="number"
                  value={newTask.sequence_no == null ? "" : String(newTask.sequence_no)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewTask({
                      ...newTask,
                      sequence_no: v === "" ? null : parseInt(v, 10),
                    });
                  }}
                  placeholder="auto (e.g., 10, 20, 30)"
                />
                <div className="flex items-center" style={{ paddingTop: 24 }}>
                  <FormControl.Checkbox
                    label="Is Milestone"
                    checked={newTask.is_milestone}
                    onChange={(checked) => setNewTask({ ...newTask, is_milestone: checked })}
                    data-testid="CHK_pm_task_create_milestone"
                  />
                </div>
              </form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  type="submit"
                  form="create-task-form"
                  variant="fill"
                  icon={Save}
                  disabled={submittingTask || !newTask.name}
                  data-testid="BTN_pm_task_create_submit"
                >
                  {submittingTask ? "Submitting..." : "Submit"}
                </Button>
              </Modal.Footer>
            </Modal>
            {extraTabs?.find((t) => t.id === activeTab)?.render()}
          </div>
        </Modal.Body>
        {activeTab === "DETAILS" && (
          <Modal.Footer>
            {isEditing ? (
              <>
                <Button
                  variant="update"
                  icon={Check}
                  type="button"
                  disabled={submitting || !editFormData.name}
                  onClick={() => editFormRef.current?.requestSubmit()}
                  data-testid="BTN_pm_project_details_update"
                >
                  {submitting ? "Updating..." : "Update"}
                </Button>
              </>
            ) : (
              <Button
                variant="fill"
                icon={Edit2}
                type="button"
                onClick={() => setIsEditing(true)}
                data-testid="BTN_pm_project_details_edit"
              >
                Edit
              </Button>
            )}
          </Modal.Footer>
        )}
      </Modal>


      {/* Edit Task Modal */}
      {editingTask && (
        <Modal
          isOpen={isEditTaskOpen}
          onClose={() => setIsEditTaskOpen(false)}
          size="lg"
        >
          <Modal.Header>Edit Task</Modal.Header>
          <Modal.Body>
            <form
              id="edit-task-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdateTask();
              }}
              data-testid="MOD_pm_task_edit_form"
            >
              <div className="app-modal-tabs">
                {["GENERAL", "PREDECESSORS", "RESOURCES", "BOOKING"].map(
                  (tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setEditTab(tab)}
                      className={`app-modal-tab${editTab === tab ? " app-modal-tab--active" : ""}`}
                      data-testid={`TAB_pm_task_edit_${tab}`}
                    >
                      {tab.charAt(0) + tab.slice(1).toLowerCase()}
                    </button>
                  ),
                )}
              </div>
              {editTab === "GENERAL" && (
                <div className="space-y-4">
                  {/* Action bar — top, consistent across tabs */}
                  <div className="flex justify-end gap-2">
                    {generalEditMode ? (
                      <>
                        <Button
                          variant="secondary-outline"
                          type="button"
                          onClick={cancelGeneralEdit}
                          data-testid="BTN_pm_task_edit_cancel"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="update"
                          icon={Check}
                          type="submit"
                          form="edit-task-form"
                          data-testid="BTN_pm_task_edit_submit"
                        >
                          Update
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="fill"
                        icon={Edit2}
                        type="button"
                        onClick={() => setGeneralEditMode(true)}
                        data-testid="BTN_pm_task_edit_enter"
                      >
                        Edit Details
                      </Button>
                    )}
                  </div>

                  {!generalEditMode ? (
                    <div className="space-y-5">
                      <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
                        <KV label="Task Name" value={editingTask.name} full />
                        <KV label="Description" value={editingTask.description} full />
                        <KV label="Duration" value={`${editingTask.duration_planned} d`} />
                        <KV label="Milestone" value={editingTask.is_milestone ? "Yes" : "No"} />
                        <KV label="Optimistic (O)" value={editingTask.optimistic != null ? `${editingTask.optimistic} d` : null} />
                        <KV label="Pessimistic (P)" value={editingTask.pessimistic != null ? `${editingTask.pessimistic} d` : null} />
                        <KV label="WBS Code" value={editingTask.wbs_code} />
                        <KV label="Parent" value={tasks.find((t) => t.id === editingTask.parent_id)?.name ?? "— (root)"} />
                        <KV label="Sequence" value={editingTask.sequence_no != null ? `#${editingTask.sequence_no}` : null} />
                      </dl>
                      <div className="pt-3 border-t">
                        <h4 className="text-sm font-bold text-gray-700 mb-3">Schedule</h4>
                        <dl className="grid grid-cols-3 gap-x-6 gap-y-3">
                          <KV label="Early Start" value={formatCPMDate(editingTask.early_start)} />
                          <KV label="Late Start" value={formatCPMDate(editingTask.late_start)} />
                          <KV label="Actual Start" value={editingTask.actual_start_date ? formatCPMDate(editingTask.actual_start_date) : null} />
                          <KV label="Early Finish" value={formatCPMDate(editingTask.early_finish)} />
                          <KV label="Late Finish" value={formatCPMDate(editingTask.late_finish)} />
                          <KV label="Actual Finish" value={editingTask.actual_finish_date ? formatCPMDate(editingTask.actual_finish_date) : null} />
                        </dl>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                  <ValidatedInput
                    vh={vhEditTask}
                    name="edit_name"
                    code={109}
                    label="Task Name"
                    required
                    value={editingTask.name}
                    onChange={(e) =>
                      setEditingTask({ ...editingTask, name: e.target.value })
                    }
                    data-testid="INP_pm_task_edit_name"
                  />
                  <ValidatedTextarea
                    vh={vhEditTask}
                    name="edit_description"
                    code={110}
                    label="Description"
                    value={editingTask.description || ""}
                    onChange={(e) =>
                      setEditingTask({
                        ...editingTask,
                        description: e.target.value,
                      })
                    }
                    rows={4}
                    data-testid="INP_pm_task_edit_description"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <ValidatedInput
                      vh={vhEditTask}
                      name="edit_duration_planned"
                      code={206}
                      label="Duration (Days)"
                      type="number"
                      value={editingTask.duration_planned}
                      onChange={(e) =>
                        setEditingTask({
                          ...editingTask,
                          duration_planned: parseFloat(e.target.value),
                        })
                      }
                      disabled={editingTask.is_milestone}
                      data-testid="INP_pm_task_edit_duration"
                    />
                    <div className="flex items-end pb-2">
                      <FormControl.Checkbox
                        label="Is Milestone"
                        checked={editingTask.is_milestone}
                        onChange={(checked) =>
                          setEditingTask({
                            ...editingTask,
                            is_milestone: checked,
                          })
                        }
                        data-testid="CHK_pm_task_edit_milestone"
                      />
                    </div>
                  </div>
                  {/* PERT (D9) — Most Likely IS the planned duration above, so we
                      only ask for Optimistic & Pessimistic. */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormControl.Input
                      label="Optimistic (O, days)" type="number" min="0" step="0.5"
                      value={editingTask.optimistic ?? ""}
                      onChange={(e) => setEditingTask({ ...editingTask, optimistic: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      data-testid="INP_pm_task_edit_optimistic"
                    />
                    <FormControl.Input
                      label="Pessimistic (P, days)" type="number" min="0" step="0.5"
                      value={editingTask.pessimistic ?? ""}
                      onChange={(e) => setEditingTask({ ...editingTask, pessimistic: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      data-testid="INP_pm_task_edit_pessimistic"
                    />
                  </div>
                  {editingTask.optimistic != null && editingTask.pessimistic != null && editingTask.duration_planned != null && (
                    <div className="text-xs text-gray-500">
                      PERT expected = {((Number(editingTask.optimistic) + 4 * Number(editingTask.duration_planned) + Number(editingTask.pessimistic)) / 6).toFixed(2)} d · σ = {(Math.abs(Number(editingTask.pessimistic) - Number(editingTask.optimistic)) / 6).toFixed(2)} — Most Likely = Duration ({editingTask.duration_planned}d); drives the schedule when O &amp; P are set.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <FormControl.Input
                      label="WBS Code"
                      maxLength={50}
                      value={editingTask.wbs_code || ""}
                      onChange={(e) =>
                        setEditingTask({
                          ...editingTask,
                          wbs_code: e.target.value,
                        })
                      }
                      data-testid="INP_pm_task_edit_wbs"
                    />
                    <FormControl.SearchSelect
                      label="Parent Task"
                      data-testid="SEL_pm_task_edit_parent"
                      options={(() => {
                        const banned = getDescendantIds(editingTask.id);
                        banned.add(editingTask.id);
                        return [
                          { value: "", label: "-- No Parent (Root) --" },
                          ...tasks
                            .filter((t) => !banned.has(t.id))
                            .map((t) => ({
                              value: String(t.id),
                              label: `${t.wbs_code ? t.wbs_code + " · " : ""}${t.name}${isOrphan(t.id) ? " (orphan — fix first)" : ""}`,
                              disabled: isOrphan(t.id),
                            })),
                        ];
                      })()}
                      value={
                        editingTask.parent_id
                          ? String(editingTask.parent_id)
                          : null
                      }
                      onChange={(v) =>
                        setEditingTask({
                          ...editingTask,
                          parent_id: v ? parseInt(String(v)) : null,
                        })
                      }
                      placeholder="-- No Parent (Root) --"
                      clearable
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormControl.SearchSelect
                      label={`Swap to Sequence${editingTask.sequence_no != null ? ` (currently #${editingTask.sequence_no})` : ""}`}
                      data-testid="SEL_pm_task_edit_swap_seq"
                      options={tasks
                        .filter(
                          (t) =>
                            (t.parent_id ?? null) === (editingTask.parent_id ?? null) &&
                            t.id !== editingTask.id &&
                            t.sequence_no != null,
                        )
                        .sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0))
                        .map((t) => ({
                          value: String(t.sequence_no),
                          label: `#${t.sequence_no} · ${t.name}`,
                        }))}
                      value={swapToSeq || null}
                      onChange={(v) => setSwapToSeq(v ? String(v) : "")}
                      placeholder={
                        tasks.some(
                          (t) =>
                            (t.parent_id ?? null) === (editingTask.parent_id ?? null) &&
                            t.id !== editingTask.id,
                        )
                          ? "Pick a sibling to swap position with"
                          : "No other tasks under this parent"
                      }
                      clearable
                    />
                    <div />
                  </div>

                  {/* Schedule — system-derived planned dates are read-only; the
                      actuals are the only date inputs. */}
                  <div className="pt-3 border-t">
                    <h4 className="text-sm font-bold text-gray-700 mb-3">
                      Schedule
                    </h4>
                    <div className="grid grid-cols-2 gap-8">
                      {/* Start */}
                      <div className="space-y-3">
                        <span className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                          Start
                        </span>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-blue-50 px-3 py-2 rounded border border-blue-100">
                            <span className="block text-[10px] uppercase text-gray-500">Early Start</span>
                            <span className="text-sm text-blue-700 font-medium">{formatCPMDate(editingTask.early_start)}</span>
                          </div>
                          <div className="bg-orange-50 px-3 py-2 rounded border border-orange-100">
                            <span className="block text-[10px] uppercase text-gray-500">Late Start</span>
                            <span className="text-sm text-orange-700 font-medium">{formatCPMDate(editingTask.late_start)}</span>
                          </div>
                        </div>
                        <ValidatedDatePicker
                          vh={vhEditTask}
                          name="edit_actual_start_date"
                          code={305}
                          label="Actual Start"
                          value={editingTask.actual_start_date ? editingTask.actual_start_date.split("T")[0] : ""}
                          onChange={(e) => setEditingTask({ ...editingTask, actual_start_date: e.target.value })}
                          data-testid="INP_pm_task_edit_actual_start"
                        />
                        {editingTask.actual_start_date &&
                          editingTask.late_start &&
                          new Date(editingTask.actual_start_date) > new Date(editingTask.late_start) && (
                            <div className="bg-red-50 text-red-700 text-xs p-2 rounded border border-red-100 flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 shrink-0" />
                              <div>
                                <strong>Delayed Start!</strong>
                                <p>Started after its Late Start date — may impact the Critical Path.</p>
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Finish */}
                      <div className="space-y-3">
                        <span className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                          Finish
                        </span>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-blue-50 px-3 py-2 rounded border border-blue-100">
                            <span className="block text-[10px] uppercase text-gray-500">Early Finish</span>
                            <span className="text-sm text-blue-700 font-medium">{formatCPMDate(editingTask.early_finish)}</span>
                          </div>
                          <div className="bg-orange-50 px-3 py-2 rounded border border-orange-100">
                            <span className="block text-[10px] uppercase text-gray-500">Late Finish</span>
                            <span className="text-sm text-orange-700 font-medium">{formatCPMDate(editingTask.late_finish)}</span>
                          </div>
                        </div>
                        <ValidatedDatePicker
                          vh={vhEditTask}
                          name="edit_actual_finish_date"
                          code={305}
                          label="Actual Finish"
                          value={editingTask.actual_finish_date ? editingTask.actual_finish_date.split("T")[0] : ""}
                          onChange={(e) => setEditingTask({ ...editingTask, actual_finish_date: e.target.value })}
                          data-testid="INP_pm_task_edit_actual_finish"
                        />
                        {editingTask.actual_finish_date &&
                          editingTask.late_finish &&
                          new Date(editingTask.actual_finish_date) > new Date(editingTask.late_finish) && (
                            <div className="bg-red-50 text-red-700 text-xs p-2 rounded border border-red-100 flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 shrink-0" />
                              <div>
                                <strong>Delayed Finish!</strong>
                                <p>Finished after its Late Finish date — likely delays successors.</p>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                    </div>
                  )}
                </div>
              )}

              {editTab === "RESOURCES" && (
                <div className="space-y-4">
                  {/* Header: title + Add button */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-gray-700">
                        Assigned Resources
                      </h4>
                      <p className="text-xs text-gray-400">
                        People and machines budgeted for this task.
                      </p>
                    </div>
                    <Button
                      variant="fill"
                      icon={Plus}
                      onClick={() => {
                        setNewResource({
                          resource_type: "STAFF",
                          resource_id: 0,
                          quantity: 1,
                          effort_hours: 0,
                        });
                        vhEditTask.reset();
                        setIsAddResourceOpen(true);
                      }}
                      data-testid="BTN_pm_resource_add_open"
                    >
                      Add Resource
                    </Button>
                  </div>

                  {/* Simple budget listing */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                        <tr>
                          <th className="p-3">Resource</th>
                          <th className="p-3 text-center">Type</th>
                          <th className="p-3 text-right">Qty (Hrs)</th>
                          <th className="p-3 text-right">Rate</th>
                          <th className="p-3 text-right">Budget</th>
                          <th className="p-3 text-center" style={{ width: 48 }}></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {resources.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="p-6 text-center text-gray-400 italic"
                            >
                              No resources assigned. Use “Add Resource” to
                              allocate from the project plan.
                            </td>
                          </tr>
                        ) : (
                          resources.map((res) => {
                            const isStaff =
                              res.resource_type === "STAFF" ||
                              res.resource_type === "MEN";
                            return (
                              <tr
                                key={res.resource_id}
                                className="hover:bg-gray-50"
                              >
                                <td className="p-3 font-medium text-gray-800">
                                  {res.resource_name}
                                </td>
                                <td className="p-3 text-center">
                                  <span
                                    className={`status-badge xs ${isStaff ? "info" : "warning"}`}
                                  >
                                    {isStaff ? "STAFF" : res.resource_type}
                                  </span>
                                </td>
                                <td className="p-3 text-right">
                                  {res.quantity}
                                </td>
                                <td className="p-3 text-right text-gray-500">
                                  ${res.rate}
                                </td>
                                <td className="p-3 text-right font-bold text-gray-800">
                                  ${res.total_cost.toFixed(2)}
                                </td>
                                <td className="p-3 text-center">
                                  <button
                                    onClick={() =>
                                      handleDeleteResource(res.resource_id)
                                    }
                                    className="table-list-action table-list-action--delete"
                                    title="Remove resource"
                                    data-testid={`BTN_pm_resource_row_${res.resource_id}_delete`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      {resources.length > 0 && (
                        <tfoot className="bg-gray-50 border-t font-bold">
                          <tr>
                            <td
                              colSpan={4}
                              className="p-3 text-right text-gray-600"
                            >
                              Total Budget:
                            </td>
                            <td className="p-3 text-right text-blue-700">
                              $
                              {resources
                                .reduce((sum, r) => sum + r.total_cost, 0)
                                .toFixed(2)}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {editTab === "PREDECESSORS" && (
                <div className="space-y-5">
                  {/* Predecessors (defined first) — border label */}
                  <fieldset className="border border-gray-200 rounded-lg px-4 pb-3 pt-1">
                    <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                      Predecessors
                    </legend>
                    <div className="flex justify-end mb-2">
                      <Button
                        variant="fill"
                        icon={Plus}
                        onClick={() => {
                          setNewDependency({ predecessor_id: "", type: "FS", lag_days: 0 });
                          vhEditTask.reset();
                          setIsAddPredOpen(true);
                        }}
                        data-testid="BTN_pm_dependency_add_open"
                      >
                        Add Predecessor
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                          <tr>
                            <th className="p-2.5">Predecessor</th>
                            <th className="p-2.5 text-center">Type</th>
                            <th className="p-2.5 text-right">Lag</th>
                            <th className="p-2.5 text-center" style={{ width: 48 }}></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {dependencies.filter((d) => d.successor_id === editingTask.id).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="p-4 text-center text-gray-400 italic">
                                No predecessors yet.
                              </td>
                            </tr>
                          ) : (
                            dependencies
                              .filter((d) => d.successor_id === editingTask.id)
                              .map((dep) => {
                                const predTask = tasks.find((t) => t.id === dep.predecessor_id);
                                return (
                                  <tr key={dep.id} className="hover:bg-gray-50">
                                    <td className="p-2.5 font-medium text-gray-800">
                                      {predTask?.name || "Unknown Task"}
                                    </td>
                                    <td className="p-2.5 text-center">
                                      <span className="status-badge xs info">{dep.type}</span>
                                    </td>
                                    <td className="p-2.5 text-right text-gray-600">
                                      {dep.lag_days > 0 ? `+${dep.lag_days}d` : dep.lag_days < 0 ? `${dep.lag_days}d` : "—"}
                                    </td>
                                    <td className="p-2.5 text-center">
                                      <button
                                        onClick={() => handleDeleteDependency(dep.id)}
                                        className="table-list-action table-list-action--delete"
                                        title="Remove Predecessor"
                                        data-testid={`BTN_pm_dependency_row_${dep.id}_delete`}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </fieldset>

                  {/* Scheduling constraint (operates on top) — border label,
                      self-contained Save (no modal-wide Update on this tab). */}
                  <fieldset className="border border-gray-200 rounded-lg px-4 pb-4 pt-1">
                    <legend className="px-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                      Scheduling Constraint
                    </legend>
                    <div className="flex justify-end mb-2">
                      <Button
                        variant="fill"
                        icon={Check}
                        type="button"
                        onClick={handleSaveConstraint}
                        data-testid="BTN_pm_constraint_save"
                      >
                        Save Constraint
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <FormControl.SearchSelect
                        label="Constraint Type"
                        data-testid="SEL_pm_task_edit_constraint_type"
                        options={[
                          { value: "ASAP", label: "As Soon As Possible" },
                          { value: "SNET", label: "Start No Earlier Than" },
                          { value: "MSO", label: "Must Start On" },
                          { value: "MFO", label: "Must Finish On" },
                        ]}
                        value={editingTask.constraint_type}
                        onChange={(v) =>
                          setEditingTask({
                            ...editingTask,
                            constraint_type: String(v ?? "ASAP") as any,
                          })
                        }
                      />
                      <ValidatedDatePicker
                        vh={vhEditTask}
                        name="edit_constraint_date"
                        code={305}
                        label="Constraint Date"
                        data-testid="INP_pm_task_edit_constraint_date"
                        value={
                          editingTask.constraint_date
                            ? editingTask.constraint_date.split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          setEditingTask({
                            ...editingTask,
                            constraint_date: e.target.value,
                          })
                        }
                        disabled={editingTask.constraint_type === "ASAP"}
                      />
                    </div>
                  </fieldset>
                </div>
              )}


            </form>
            {/* Booking panel lives OUTSIDE the edit-task form so Enter in its
                fields books an actual instead of submitting the task update. */}
            {editTab === "BOOKING" && editingTask && (
              <div style={{ height: "60vh" }}>
                <ActualBookingPanel task={editingTask} onChanged={loadData} />
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {/* Add / Edit project Resource Plan entry — opened from the Resources tab. */}
      <Modal
        isOpen={isPlanResOpen}
        onClose={() => { setIsPlanResOpen(false); setEditingPlanId(null); }}
        size="md"
        data-testid="MOD_pm_plan_resource"
      >
        <Modal.Header>{editingPlanId ? "Edit Resource" : "Create Resource"}</Modal.Header>
        <Modal.Body>
          <form id="pm-plan-form" onSubmit={handleAddPlanResource} className="grid grid-cols-1 gap-y-4">
            <FormControl.SearchSelect
              label="Type"
              options={[
                { value: "STAFF", label: "Man (Staff)" },
                { value: "MACHINE", label: "Machine" },
              ]}
              value={newPlan.resource_kind}
              disabled={!!editingPlanId}
              onChange={(v) => setNewPlan({ ...newPlan, resource_kind: String(v ?? "STAFF") as "STAFF" | "MACHINE", user_id: "", it_asset_id: "" })}
              data-testid="SEL_pm_plan_kind"
            />
            {newPlan.resource_kind === "STAFF" ? (
              <FormControl.SearchSelect
                label="Person"
                options={planUsers
                  .filter((u: any) => editingPlanId ? true : !planRows.some((r) => r.resource_kind !== "MACHINE" && r.user_id === u.id))
                  .map((u: any) => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))}
                value={newPlan.user_id === "" ? null : newPlan.user_id}
                disabled={!!editingPlanId}
                onChange={(v) => setNewPlan({ ...newPlan, user_id: v ? Number(v) : "" })}
                placeholder="— pick a person —"
                clearable
                data-testid="SEL_pm_plan_user"
              />
            ) : (
              <FormControl.SearchSelect
                label="Machine"
                options={assetOptions
                  .filter((a: any) => editingPlanId ? true : !planRows.some((r) => r.resource_kind === "MACHINE" && r.it_asset_id === a.id))
                  .map((a: any) => ({ value: a.id, label: a.asset_name }))}
                value={newPlan.it_asset_id === "" ? null : newPlan.it_asset_id}
                disabled={!!editingPlanId}
                onChange={(v) => setNewPlan({ ...newPlan, it_asset_id: v ? Number(v) : "" })}
                placeholder="— pick a machine —"
                clearable
                data-testid="SEL_pm_plan_asset"
              />
            )}
            {newPlan.resource_kind === "STAFF" && (
              <FormControl.Input
                label="Role"
                value={newPlan.role}
                onChange={(e) => setNewPlan({ ...newPlan, role: e.target.value })}
                disabled={!!editingPlanId && !isPlanEditing}
                data-testid="INP_pm_plan_role"
              />
            )}
            <div className="grid grid-cols-2 gap-4">
              <FormControl.Input
                label="Rate / hr"
                type="number"
                min="0"
                step="0.01"
                value={newPlan.rate}
                onChange={(e) => setNewPlan({ ...newPlan, rate: e.target.value === "" ? "" : Number(e.target.value) })}
                disabled={!!editingPlanId && !isPlanEditing}
                data-testid="INP_pm_plan_rate"
              />
              <FormControl.Input
                label="Budgeted Hrs"
                type="number"
                min="0"
                step="0.5"
                value={newPlan.hours}
                onChange={(e) => setNewPlan({ ...newPlan, hours: e.target.value === "" ? "" : Number(e.target.value) })}
                disabled={!!editingPlanId && !isPlanEditing}
                data-testid="INP_pm_plan_hours"
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className={editingPlanId ? "app-modal-footer--split" : ""}>
          {editingPlanId && (
            <Button
              variant="delete-outline"
              icon={Trash2}
              type="button"
              onClick={async () => {
                const removed = await handleRemovePlanResource(editingPlanId);
                if (removed) { setIsPlanResOpen(false); setEditingPlanId(null); }
              }}
              data-testid="BTN_pm_plan_delete"
            >
              Delete
            </Button>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            {editingPlanId && !isPlanEditing ? (
              <Button
                key="edit"
                variant="fill"
                icon={Edit2}
                type="button"
                onClick={() => setIsPlanEditing(true)}
                data-testid="BTN_pm_plan_edit"
              >
                Edit
              </Button>
            ) : (
              <Button
                key="save"
                type="submit"
                form="pm-plan-form"
                variant={editingPlanId ? "update" : "fill"}
                icon={editingPlanId ? Check : Save}
                disabled={newPlan.resource_kind === "STAFF" ? !newPlan.user_id : !newPlan.it_asset_id}
                data-testid="BTN_pm_plan_add"
              >
                {editingPlanId ? "Update" : "Submit"}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Add Predecessor — opened from the Edit Task → Predecessors tab. */}
      {editingTask && (
        <Modal
          isOpen={isAddPredOpen}
          onClose={() => setIsAddPredOpen(false)}
          size="md"
          data-testid="MOD_pm_dependency_add"
        >
          <Modal.Header>Add Predecessor</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <FormControl.SearchSelect
                label="Predecessor Task"
                data-testid="SEL_pm_dependency_predecessor"
                options={tasks
                  .filter(
                    (t) =>
                      t.id !== editingTask.id &&
                      !dependencies.some(
                        (d) =>
                          d.successor_id === editingTask.id &&
                          d.predecessor_id === t.id,
                      ),
                  )
                  .map((t) => ({ value: String(t.id), label: t.name }))}
                value={newDependency.predecessor_id || null}
                onChange={(v) =>
                  setNewDependency({ ...newDependency, predecessor_id: v ? String(v) : "" })
                }
                placeholder="Select a task..."
                clearable
              />
              <div className="grid grid-cols-2 gap-4">
                <FormControl.SearchSelect
                  label="Dependency Type"
                  data-testid="SEL_pm_dependency_type"
                  options={[
                    { value: "FS", label: "FS — Finish to Start" },
                    { value: "SS", label: "SS — Start to Start" },
                    { value: "FF", label: "FF — Finish to Finish" },
                    { value: "SF", label: "SF — Start to Finish" },
                  ]}
                  value={newDependency.type}
                  onChange={(v) =>
                    setNewDependency({ ...newDependency, type: String(v ?? "FS") })
                  }
                />
                <ValidatedInput
                  vh={vhEditTask}
                  name="lag_days"
                  code={201}
                  label="Lag (days)"
                  type="number"
                  value={newDependency.lag_days}
                  onChange={(e) =>
                    setNewDependency({ ...newDependency, lag_days: parseInt(e.target.value) || 0 })
                  }
                  placeholder="0"
                  data-testid="INP_pm_dependency_lag"
                />
              </div>
              <p className="text-[11px] text-gray-400">
                FS Finish-Start · SS Start-Start · FF Finish-Finish · SF Start-Finish.
                Lag adds days after the link (negative = lead/overlap).
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary-outline"
              type="button"
              onClick={() => setIsAddPredOpen(false)}
              data-testid="BTN_pm_dependency_cancel"
            >
              Cancel
            </Button>
            <Button
              variant="fill"
              icon={Plus}
              type="button"
              onClick={handleAddDependency}
              disabled={!newDependency.predecessor_id}
              data-testid="BTN_pm_dependency_add"
            >
              Add Predecessor
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Add Resource — opened from the Edit Task → Resources tab. Lives at the
          top level (outside the edit-task form) so it stacks cleanly. */}
      <Modal
        isOpen={isAddResourceOpen}
        onClose={() => setIsAddResourceOpen(false)}
        size="md"
        data-testid="MOD_pm_resource_add"
      >
        <Modal.Header>Add Resource</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <FormControl.SearchSelect
              label="Type"
              data-testid="SEL_pm_resource_type"
              options={[
                { value: "STAFF", label: "Staff" },
                { value: "MACHINE", label: "Machine (IT Asset)" },
              ]}
              value={newResource.resource_type}
              onChange={(v) =>
                setNewResource({
                  ...newResource,
                  resource_type: String(v ?? "STAFF") as
                    | "STAFF"
                    | "MEN"
                    | "MACHINE"
                    | "MATERIAL",
                  resource_id: 0,
                })
              }
            />
            <FormControl.SearchSelect
              label="Resource Name"
              data-testid="SEL_pm_resource_name"
              options={availableResourceOptions}
              value={newResource.resource_id || null}
              onChange={(v) =>
                setNewResource({
                  ...newResource,
                  resource_id: v ? Number(v) : 0,
                })
              }
              placeholder={
                availableResourceOptions.length
                  ? "-- Select Resource --"
                  : (newResource.resource_type === "STAFF"
                    ? staffOptions
                    : taskMachineOptions
                  ).length
                    ? "All plan resources of this type are already added"
                    : "Nothing on the project plan for this type"
              }
              clearable
            />
            <ValidatedInput
              vh={vhEditTask}
              name="newResource_quantity"
              code={206}
              label="Quantity (Hours)"
              type="number"
              value={newResource.quantity}
              onChange={(e) =>
                setNewResource({
                  ...newResource,
                  quantity: parseFloat(e.target.value),
                })
              }
              data-testid="INP_pm_resource_quantity"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary-outline"
            onClick={() => setIsAddResourceOpen(false)}
            data-testid="BTN_pm_resource_add_cancel"
          >
            Cancel
          </Button>
          <Button
            variant="fill"
            icon={Plus}
            onClick={handleAddResource}
            disabled={!newResource.resource_id}
            data-testid="BTN_pm_resource_add"
          >
            Add Resource
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast toast={planToast} onClose={() => setPlanToast(null)} />
    </>
  );
};
